import { defineStore } from 'pinia';
import productAdvisor from '@/services/productAdvisor';
import _ from 'lodash';
import statsService from '@/services/stats';

/** Product advisor as returned by the backend (loose shape, backend-owned) */
interface Advisor {
  /** Backend document id */
  _id: string;
  /** Bot this advisor belongs to */
  botId: string;
  /** Products configured for this advisor */
  products: any[];
  /** Additional backend fields */
  [key: string]: any;
}

interface ProductAdvisorState {
  /** True while init() is fetching advisors */
  isLoading: boolean;
  /** All advisors of this bot */
  advisors: Advisor[];
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/productAdvisor.ts` (namespace
 * `productAdvisor`). In-memory only — no `vuexPersist` option.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had both a state prop `isLoading` and a getter `isLoading` (identity passthrough).
 *   Renamed getter → `isLoadingGetter`.
 * - `updateProduct` existed as BOTH an action (fetches from backend, then commits) and a mutation
 *   of the same name → merged into a single action.
 * - `deleteProduct` existed as BOTH an action (deletes on backend, then commits) and a mutation of
 *   the same name → merged into a single action.
 * - `setAdvisors`, `addAdvisor`, `addProduct`, `updateAdvisor` existed only as mutations → became
 *   same-named actions (no collision, since no action of those names existed).
 * - `addAdvisor` mutation is not called from any action in this module — kept as
 *   intentionally-dead code, exactly as in the legacy module.
 * - No cross-namespace (`rootGetters`/`dispatch`/`commit` with `{root:true}`) access existed in
 *   this module — everything is local, so no sibling-store composables are needed here.
 */
export const useProductAdvisorStore = defineStore('productAdvisor', {
  state: (): ProductAdvisorState => ({
    isLoading: false,
    // All advisors of this bot
    advisors: [],
  }),
  getters: {
    /**
     * Renamed from legacy getter `isLoading` (collides with the state prop of the same name — the
     * state prop keeps the name).
     */
    isLoadingGetter: (state): boolean => state.isLoading,
    getAdvisors: (state) => (botId: string) => state.advisors.filter(advisor => advisor.botId === botId),
    getAdvisor: (state) => (botId: string, id: string) => state.advisors.find(advisor => advisor._id === id && advisor.botId === botId),
  },
  actions: {
    /**
     * Gets advisor by ID from cache, and if necessary, fetches all advisors then tries again
     * @param {string} botId
     * @param {string} advisorId
     * @returns {Promise<Object|null>} If null, there is no such advisor in backend either
     */
    async getOrFetchAdvisor({ botId, advisorId }: { botId: string; advisorId: string }) {
      const advisor = this.getAdvisor(botId, advisorId);
      if (advisor) return advisor;
      await this.fetchAdvisors(botId);
      return this.getAdvisor(botId, advisorId);
    },
    /**
     * Performs the necessary steps to initialize this store module
     * @param {string} botId Current bot ID
     * @returns {Promise<void>}
     */
    async init({ botId }: { botId: string }) {
      this.isLoading = true;
      await this.fetchAdvisors(botId);
      this.isLoading = false;
    },
    /**
     * Fetches advisors from backend and updates local state
     * @param {string} botId Bot ID to get advisors of
     * @returns {Promise<void>}
     */
    async fetchAdvisors(botId: string) {
      const advisors = await productAdvisor.getAdvisors(botId);
      if (advisors === null) return;
      this.setAdvisors(advisors);
    },
    /**
     * Updates an advisor in the backend and local state
     * @param {Object} payload
     */
    async patchAdvisor({ botId, advisorId, newProperties }: { botId: string; advisorId: string; newProperties: any }) {
      const savedAdvisor = await productAdvisor.updateAdvisor(botId, advisorId, newProperties);
      this.updateAdvisor(savedAdvisor);
    },

    /**
     * Create a new product for the given advisor
     * @param {Object} payload
     */
    async createProduct({ botId, advisorId, product }: { botId: string; advisorId: string; product: any }) {
      const savedProduct = await productAdvisor.createProduct(botId, advisorId, product);
      this.addProduct({ advisorId, product: savedProduct });
    },

    /**
     * Update a product
     *
     * Merged from legacy action `updateProduct` (fetches the saved product from backend, then
     * committed the mutation below) + legacy mutation `updateProduct` (same name collision).
     * @param {Object} payload
     */
    async updateProduct({ botId, advisorId, productId, product }: { botId: string; advisorId: string; productId: string; product: any }) {
      const savedProduct = await productAdvisor.updateProduct(botId, advisorId, productId, product);
      // Merged from legacy mutation `updateProduct`. NOTE: preserves legacy behavior of applying
      // `product` (the input) rather than `savedProduct` (the backend response) — kept as-is.
      void savedProduct;
      const advisor = this.advisors.find(a => a._id === advisorId);
      if (advisor) {
        const foundProduct = advisor.products.find((p: any) => p._id === productId);
        if (foundProduct) {
          Object.assign(foundProduct, _.cloneDeep(product));
          this.advisors = _.cloneDeep(this.advisors); // reassign the whole advisors array to trigger reactivity
        }
      }
    },
    /**
     * Delete a product
     *
     * Merged from legacy action `deleteProduct` (deletes on backend, then committed the mutation
     * below) + legacy mutation `deleteProduct` (same name collision).
     * @param {Object} payload
     */
    async deleteProduct({ botId, advisorId, productId }: { botId: string; advisorId: string; productId: string }) {
      await productAdvisor.deleteProduct(botId, advisorId, productId);
      // Merged from legacy mutation `deleteProduct`.
      const advisor = this.advisors.find(a => a._id === advisorId);
      if (advisor) {
        advisor.products = advisor.products.filter((p: any) => p._id !== productId);
      }
    },

    async getAmountOfStartedConversations({ botId, intent, from, to }: any) {
      const data = await statsService.productAdvisor.getAmountOfStartedConsultations({ botId, intent, from, to });
      // Save data if needed
      return data;
    },

    async getConversionRate({ botId, intent, from, to }: any) {
      const data = await statsService.productAdvisor.getConversionRate({ botId, intent, from, to });
      // Save data if needed
      return data;
    },

    // TODO: Finish others
    async getMostPopularProducts({ botId, intent, from, to, limit }: any) {
      const data = await statsService.productAdvisor.getPopularProducts({ botId, intent, from, to, limit });
      // Save data if needed
      return data;
    },
    async getMostRecommendedProducts({ botId, intent, from, to, limit }: any) {
      const data = await statsService.productAdvisor.getRecommendedProducts({ botId, intent, from, to, limit });
      // Save data if needed
      return data;
    },
    async getMostRecommendedProductCount({ botId, intent, from, to, unique }: any) {},
    async getAvgAmountOfRecommendedProducts({ botId, intent, from, to }: any) {
      const data = await statsService.productAdvisor.getRecommendedProductsAvg({ botId, intent, from, to });
      // Save data if needed
      return data;
    },
    async getMostRequestedQueryParams({ botId, intent, from, to }: any) {
      // - Retrieve detailed list of most requested query parameters
      const data = await statsService.productAdvisor.getMostRequestedQueryProperties({ botId, intent, from, to });
      // Save data if needed
      return data;
    },

    /**
     * Converted from legacy mutation `setAdvisors` (no matching legacy action existed).
     * Overwrites the current advisors with the given list
     * @param {Object[]} advisors
     */
    setAdvisors(advisors: Advisor[]) {
      this.advisors = advisors;
    },
    /**
     * Converted from legacy mutation `addAdvisor` (no matching legacy action existed).
     * Not called from anywhere in this module — kept as intentionally-dead code, exactly as in
     * the legacy module.
     * Adds a new advisor to the list
     * @param {Object} advisor
     */
    addAdvisor(advisor: Advisor) {
      const foundIndex = this.advisors.findIndex(a => a._id === advisor._id);

      if (foundIndex !== -1) {
        this.advisors[foundIndex] = advisor;
      } else {
        this.advisors.push(advisor);
      }
    },
    /**
     * Converted from legacy mutation `updateAdvisor` (no matching legacy action existed; called by
     * `patchAdvisor` above via `commit('updateAdvisor', ...)`).
     */
    updateAdvisor(advisor: Advisor) {
      const foundIndex = this.advisors.findIndex(a => a._id === advisor._id);
      if (foundIndex !== -1) {
        const advisorsCopy = [...this.advisors];
        advisorsCopy[foundIndex] = { ...advisorsCopy[foundIndex], ...advisor };
        this.advisors = advisorsCopy; // reassign the whole advisors array to trigger reactivity
      }
    },
    /**
     * Converted from legacy mutation `addProduct` (no matching legacy action existed; called by
     * `createProduct` above via `commit('addProduct', ...)`).
     */
    addProduct({ advisorId, product }: { advisorId: string; product: any }) {
      const advisor = this.advisors.find(a => a._id === advisorId);
      if (advisor) {
        advisor.products.push(product);
      }
    },
  },
});
